<script lang="ts" setup>
    import '../../../../css/choice.css';
    import {onMounted,watch,reactive} from 'vue';
    import {popAlert,ModalStyles} from '../../../../src/lib/Notification';
    import {ErrorReturnMessage,getClaimHeader,getClaimProcedureList,getClaimComments} from '../../../../src/lib/Api/Claim';
    import {ClaimDisplay,IClaimTableFormat,ClaimTableWidth,ClaimColumnWidth,ClaimColumnDefinitions,amountClass,
            IClaimHeader,IClaimProcedureIds,IClaimComment,claimLegacyWindowOpen,ClaimMenuActions,ClaimSelectionType} from '../../../../src/lib/Models/ClaimModel';
    import {prettyCurrency} from '../../../../src/lib/Formatting/Number';   
    import ClaimProcedure from '../Subcomponents/ClaimProcedure.vue'; 
    import ClaimRefundIcons from '../Subcomponents/ClaimRefundIcons.vue'; 
    import ClaimRefundAmounts from '../Subcomponents/ClaimRefundAmounts.vue';
    import DropDownMenu ,{IDropDownMenu} from "../../../components/DropDownMenu.vue";
    import {initPinia} from '../../../lib/PiniaMgr';
    import {usePaymentPlanNewStore} from '../../../lib/Stores/PaymentPlanNewStore';
    import {usePaymentPlanDetailsStore} from '../../../lib/Stores/PaymentPlanDetailsStore';
    import {useRefundFullfillmentStore} from '../../../lib/Stores/RefundFullfillmentStore';

    
    const paymentPlanNewStore = usePaymentPlanNewStore(initPinia());
    const paymentPlanDetailsStore = usePaymentPlanDetailsStore(initPinia());
    const refundFullfillmentStore = useRefundFullfillmentStore(initPinia());
 
    const formData = reactive <
    {   
        claimTableFormat: IClaimTableFormat,
        claimHeaderData?: IClaimHeader,
        procedureList?: IClaimProcedureIds[],
        claimComments?: IClaimComment[],
        commentColumnCount: number,
        display: {Procedures : boolean, Payments: boolean, Comments: boolean},
        payerDisplay: {ToolTip: string, Label: string},
        claimMenu: IDropDownMenu[]
    }>({    
        claimTableFormat: {inputDisplay: false, inputLabel: "", levelDisplay: true, statusDisplay: false, payerDisplay: true, columns: 8, tableWidth: (ClaimTableWidth + ClaimColumnDefinitions.Level + ClaimColumnDefinitions.Payer)},
        commentColumnCount: 7,
        display: {Procedures : false, Payments: false, Comments: false},
        payerDisplay: {ToolTip: "", Label: ""},
        claimMenu:
            [
                {enabled: true, menuBreak: false, label: "Procedures", dataValue: ClaimMenuActions.Procedures, callBack: displayProcedures},  
                {enabled: true, menuBreak: false, label: "Payments", dataValue: ClaimMenuActions.Payments, callBack: displayPayments},  
                {enabled: true, menuBreak: false, label: "Comments", dataValue: ClaimMenuActions.Comments, callBack: displayComments},
            ]
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        claimID? : number,
        display: ClaimDisplay,
        autodistribute?: boolean,
        claimTableFormat: IClaimTableFormat,
        displayProcedures: boolean,
        displayPayments: boolean,
        displayComments: boolean 
    }>(),
    {
        saveEnabled: 0,  
        headerColumns: 0,   
        autodistribute: false, 
        displayProcedures: false,
        displayPayments: false,
        displayComments: false 
    });
    onMounted(async () => {  
        formData.claimTableFormat = props.claimTableFormat;
        formData.claimTableFormat.tableWidth -= ClaimColumnWidth;
        formData.commentColumnCount += 
            (formData.claimTableFormat.payerDisplay ? 1 : 0) + 
            (formData.claimTableFormat.inputDisplay ? 1 : 0);
        formData.display.Procedures = props.displayProcedures;   
        formData.display.Payments = props.displayPayments;     
        formData.display.Comments = props.displayComments;  
        if (props.display === ClaimDisplay.Transfer) {
            formData.claimMenu[0].enabled = false;
        }
        refreshData();
    });  
    watch(() => props.displayProcedures, (newProcedures : boolean, oldProcedures : boolean) => {  
        if (newProcedures !== oldProcedures) { 
            formData.display.Procedures = newProcedures; 
            loadProcedures();   
        }
    });
    watch(() => props.displayPayments, (newPayments : boolean, oldPayments : boolean) => { 
        if (newPayments !== oldPayments) { 
            formData.display.Payments = newPayments;
        }
    });
    watch(() => props.displayComments, (newComments : boolean, oldComments : boolean) => { 
        if (newComments !== oldComments) { 
            formData.display.Comments = newComments;
            loadComments();   
        }
    });

    async function refreshData() : Promise<void>  {
        if (!props.claimID) {  
            return;
        } 
        const claimGet = await getClaimHeader(props.claimID);
        if (!claimGet.success) {  
            await popAlert(claimGet.error?.error ?? ErrorReturnMessage.claimHeader,"Claim Details",ModalStyles.danger);
            return;
        }
        formData.claimHeaderData = claimGet.success;
        formData.payerDisplay.ToolTip = 
            `Responsible Party: ${formData.claimHeaderData?.responsiblePartyName}\r\nPrimary: ${formData.claimHeaderData?.primaryPayerName}` +
            (formData.claimHeaderData?.secondaryPayerEID === 0 ? "" : `\r\nSecondary: ${formData.claimHeaderData?.secondaryPayerName}`) +
            (formData.claimHeaderData?.tertiaryPayerEID === 0 ? "" : `\r\nTertiary: ${formData.claimHeaderData?.tertiaryPayerName}`); 
        formData.payerDisplay.Label =
            (formData.claimHeaderData?.primarySelfPay === 1 ? formData.claimHeaderData?.responsiblePartyName ?? "" : formData.claimHeaderData?.primaryPayerName ?? "");

        if (formData.display.Procedures) {
            loadProcedures();
        }
        if (formData.display.Comments) {
            loadComments();
        }
        if (formData.claimHeaderData) {
            if (props.display === ClaimDisplay.PaymentPlan) { 
                paymentPlanNewStore.updateClaim(formData.claimHeaderData.claimID ?? 0,formData.claimHeaderData.charges ?? 0,formData.claimHeaderData.payments ?? 0,formData.claimHeaderData.adjustments ?? 0,formData.claimHeaderData.claimBalance ?? 0);
            }
            else if (props.display === ClaimDisplay.PaymentPlanDetails) { 
                paymentPlanDetailsStore.updateClaim(ClaimSelectionType.Patient, formData.claimHeaderData.claimID ?? 0,formData.claimHeaderData.charges ?? 0,formData.claimHeaderData.payments ?? 0,formData.claimHeaderData.adjustments ?? 0,formData.claimHeaderData.claimBalance ?? 0);
            }
            else if (props.display === ClaimDisplay.PaymentPlanIncluded) { 
                paymentPlanDetailsStore.updateClaim(ClaimSelectionType.Included, formData.claimHeaderData.claimID ?? 0,formData.claimHeaderData.charges ?? 0,formData.claimHeaderData.payments ?? 0,formData.claimHeaderData.adjustments ?? 0,formData.claimHeaderData.claimBalance ?? 0);
            }
        }
    }
    function openClaim() : void {
        if (!props.claimID) {  
            return;
        }   
        const claimURL = `/officemd/screens/ClaimEntry.htm?ClaimID=${props.claimID}`
        claimLegacyWindowOpen("ClaimEntry", claimURL);     
    }
    function openClaimHistory() : void {
        if (!props.claimID) {  
            return;
        }   
        const claimHistoryURL = `/officemd/screens/transactionHistory.asp?ClaimID=${props.claimID}`
        claimLegacyWindowOpen("TransHistory", claimHistoryURL);     
    }
    function displayProcedures() : void {
        formData.display.Procedures = !formData.display.Procedures;
        loadProcedures();
    }
    async function loadProcedures() : Promise<void> {
        if (!props.claimID) {
            return;
        }
        if (formData.display.Procedures) {
            const procedureGet = await getClaimProcedureList(props.claimID); 
            if (!procedureGet.success) {  
                await popAlert(procedureGet.error?.error ?? ErrorReturnMessage.claimHeader,"Claim Procedure List",ModalStyles.danger);
                return;
            }
            formData.procedureList = procedureGet.success;
        }
        else {
            formData.procedureList = [];
            formData.display.Payments = false;
        }
        if (props.display === ClaimDisplay.Transfer) {
            refundFullfillmentStore.setProcedureTransferTotals(formData.procedureList.length);
        }
    }
    function displayPayments() : void {
        formData.display.Payments = !formData.display.Payments;
        if ((formData.display.Payments) && (!formData.display.Procedures)) {
            displayProcedures();
        }
    }
    function displayComments() : void {
        formData.display.Comments = !formData.display.Comments;
        loadComments();
    }
    async function loadComments() : Promise<void> {
        if (!props.claimID) {
            return;
        }
        if (formData.display.Comments) {
            const commentsGet = await getClaimComments(props.claimID);
            if (!commentsGet.success) {  
                await popAlert(commentsGet.error?.error ?? ErrorReturnMessage.claimComments,"Claim Comments",ModalStyles.danger);
                return;
            }
            formData.claimComments = commentsGet.success;
        }
        else {
            formData.claimComments = [];
        }
    }
</script> 
 
<style scoped> 
.viewDisplay {
    display: block;
}
.viewHide {
    display: none;
}
.cardActions {
    font-size: .85rem;
    font-weight: 500;
    color: #204D89;
}
.billing-header {
  color: #204D89;
  background-color: #f1f1f1;
}
.billing-data-left {
  color: black;
  background-color: white;
  text-align: left;
}    
.billing-data-center {
  color: black;
  background-color: white;
  text-align: center;
}
.billing-data-right {
  color: black;
  background-color: white;
  text-align: right;
} 
.billing-text {
  FONT-SIZE: 8pt;
  font-family: Arial,  sans-serif, Helvetica;
  padding: 5px 5px 5px 5px;
  cursor: default;
  vertical-align: middle;
}
.billing-reversal { 
    text-decoration: line-through; 
}
.billing-break {
    border-top: 1px solid silver;
}
</style>
<template>
    <table border="0" aria-hidden="true" :style="`table-layout: fixed; border-collapse: collapse;  width: ${formData.claimTableFormat.tableWidth}rem;`">
        <colgroup>
            <col :style="`width: ${ClaimColumnDefinitions.ClaimID - ClaimColumnWidth}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.DOS}rem;`"/>
            <col v-if="formData.claimTableFormat.levelDisplay" :style="`width: ${ClaimColumnDefinitions.Level}rem;`"/>
            <col v-if="formData.claimTableFormat.statusDisplay" :style="`width: ${ClaimColumnDefinitions.Status}rem;`"/>
            <col v-if="formData.claimTableFormat.payerDisplay" :style="`width: ${ClaimColumnDefinitions.Payer}rem;`"/> 
            <col :style="`width: ${ClaimColumnDefinitions.Charges}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.Payments}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.Adjustments}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.Balance}rem;`"/>
            <col v-if="formData.claimTableFormat.inputDisplay" :style="`width: ${ClaimColumnDefinitions.Input}rem;`"/>
        </colgroup>
        <tr class="billing-text">
            <td> 
                <span @click="openClaim" class="isalus-link">
                    {{formData.claimHeaderData?.claimID ?? 0}}
                </span>
                <DropDownMenu 
                    :dropDownMenu="formData.claimMenu"
                    :menuFooter="'Selecting one of the items above will toggle the current claim display accordingly.'"
                />
            </td>
            <td class="text-center" :title="`ID: ${formData.claimHeaderData?.claimID}\r\nDOS: ${formData.claimHeaderData?.dos}\r\nRendering: ${formData.claimHeaderData?.renderingProviderName}\r\Location: ${formData.claimHeaderData?.serviceLocationName}`">
                <span @click="openClaimHistory" class="isalus-link">
                    {{formData.claimHeaderData?.dos ?? ""}}
                </span>
            </td>
            <td v-if="formData.claimTableFormat.levelDisplay" :title="`Level: ${formData.claimHeaderData?.levelDescription}\r\nStatus: ${formData.claimHeaderData?.statusDescription}`">{{formData.claimHeaderData?.levelDescription ?? ""}}</td>
            <td v-if="formData.claimTableFormat.statusDisplay" :title="`Status: ${formData.claimHeaderData?.statusDescription}\r\Level: ${formData.claimHeaderData?.levelDescription}`">{{formData.claimHeaderData?.statusDescription ?? ""}}</td>
            <td v-if="formData.claimTableFormat.payerDisplay" class="text-truncate" :title="`${formData.payerDisplay.ToolTip}`">
                <div :style="`width: ${ClaimColumnDefinitions.Input}rem;`">{{formData.payerDisplay.Label}}</div>
            </td>
            <td :class="amountClass(formData.claimHeaderData?.charges,0,false)" 
                :title="`Charges: ${prettyCurrency(formData.claimHeaderData?.charges ?? 0)}`">
                {{prettyCurrency((formData.claimHeaderData?.charges ?? 0),{negativeCurrencyDisplay: true})}}
            </td>
            <td :class="amountClass(formData.claimHeaderData?.payments,0,false)" 
                :title="`Payments: ${prettyCurrency(formData.claimHeaderData?.payments ?? 0)}`">
                {{prettyCurrency((formData.claimHeaderData?.payments ?? 0),{negativeCurrencyDisplay: true})}}
            </td>
            <td :class="amountClass(formData.claimHeaderData?.adjustments,0,false)" 
                :title="`Adjustments: ${prettyCurrency(formData.claimHeaderData?.adjustments ?? 0)}`">
                {{prettyCurrency((formData.claimHeaderData?.adjustments ?? 0),{negativeCurrencyDisplay: true})}}
            </td>
            <td :class="amountClass(formData.claimHeaderData?.claimBalance,0,false)" 
                :title="`Balance: ${prettyCurrency(formData.claimHeaderData?.claimBalance ?? 0)}`">
                {{prettyCurrency((formData.claimHeaderData?.claimBalance ?? 0),{negativeCurrencyDisplay: true})}}
            </td> 
            <td class="ps-1 text-center label-isalus-small"> 
                <div v-if="props.display === ClaimDisplay.CreditPayment">
                    <ClaimRefundIcons
                        :claimID="props.claimID"
                        :claimBalance="formData.claimHeaderData?.claimBalance"
                        :responsiblePartyEID="formData.claimHeaderData?.responsiblePartyEID"
                        :primaryPayerEID="formData.claimHeaderData?.primaryPayerEID"
                        :secondaryPayerEID="formData.claimHeaderData?.secondaryPayerEID"
                        :tertiaryPayerEID="formData.claimHeaderData?.tertiaryPayerEID"
                    />
                </div>
                <div v-if="(props.display === ClaimDisplay.Refund || props.display === ClaimDisplay.RefundTransfer)">
                    <ClaimRefundAmounts
                        :autoDistribute="props.autodistribute"
                        :claimID="props.claimID"
                        :display="props.display"
                        :claimBalance="formData.claimHeaderData?.claimBalance"
                        :responsiblePartyEID="formData.claimHeaderData?.responsiblePartyEID"
                        :responsiblePartyName="formData.claimHeaderData?.responsiblePartyName"
                        :primaryPayerEID="formData.claimHeaderData?.primaryPayerEID"
                        :secondaryPayerEID="formData.claimHeaderData?.secondaryPayerEID"
                        :tertiaryPayerEID="formData.claimHeaderData?.tertiaryPayerEID"
                    />
                </div>
                <div v-if="props.display === ClaimDisplay.Transfer">
                    &nbsp;
                </div>
            </td> 
            <td v-if="!formData.claimTableFormat.inputDisplay">&nbsp;</td>
        </tr>
        <tr v-if="(formData.procedureList?.length ?? 0) > 0" v-for="(procedureRecord,index) in formData.procedureList" :key="`PRC${procedureRecord.diagnosisID}`">   
            <td :colspan="formData.claimTableFormat.columns">
                <ClaimProcedure 
                    :saveEnabled="props.saveEnabled"
                    :procedureIds="procedureRecord" 
                    :display="props.display"
                    :claimTableFormat="formData.claimTableFormat"
                    :displayPayments = "formData.display.Payments"
                    :displayComments = "formData.display.Comments"
                />
            </td>   
        </tr>  
        <tr class="text-muted fst-italic billing-text" v-if="(formData.claimComments?.length ?? 0) > 0" v-for="(claimCommentRecord,index) in formData.claimComments" :key="`CMT${claimCommentRecord.commentID}`">
            <td :class="(index === 0 ? 'billing-break' : '')">{{claimCommentRecord.enterDate}}</td>
            <td :class="(index === 0 ? 'billing-break' : '')">{{claimCommentRecord.userID}}</td>
            <td :class="(index === 0 ? 'billing-break' : '')" :colspan="formData.commentColumnCount">{{claimCommentRecord.comment}}</td> 
        </tr>
    </table>    
</template>
